import React, { useState } from 'react';

import { IconButton, Typography } from '@mui/material';
import type { GridCellParams } from '@mui/x-data-grid-premium';
import { AlineTooltip, ConfirmationDialog } from 'src/components';
import { CheckIcon } from 'src/components/icons/CheckIcon';
import { RejectChangeXIcon } from 'src/components/icons/RejectChangeXIcon';
import type { ReminderListRow } from 'src/components/reminders/ColumnDef';
import { CompleteReminderButton } from 'src/components/reminders/CompleteReminderButton';
import { Apollo } from 'src/gql/generated';
import { useAcceptReminderWithCacheUpdate, useRejectReminderWithCacheUpdate } from 'src/hooks';
import { createClampStyles } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  actionButton: {
    padding: theme.spacing(0.25),
  },
  actions: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  container: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    justifyContent: 'center',
    minWidth: 0,
    overflow: 'hidden',
    width: '100%',
  },
  row: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minWidth: 0,
    overflow: 'hidden',
    width: '100%',
  },
  title: {
    ...createClampStyles(3),
    flex: 1,
  },
}));

export function ReminderTitleCell({
  row,
  value,
}: GridCellParams<ReminderListRow, ReminderListRow['title']>): JSX.Element {
  const { classes } = useStyles();
  const reminderTitle = value || 'Untitled';
  const isCompleted = Boolean(row.completedAt);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);
  const isPendingReview = row.reviewStatus === Apollo.ReminderReviewStatus.PendingReview;
  const { acceptReminder, loading: acceptLoading } = useAcceptReminderWithCacheUpdate();
  const { loading: rejectLoading, rejectReminder } = useRejectReminderWithCacheUpdate();

  const handleAccept = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await acceptReminder({ variables: { reminderId: row.id } });
  };

  const handleRejectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRejectConfirmOpen(true);
  };

  const handleRejectConfirm = async () => {
    await rejectReminder({ variables: { reminderId: row.id } });
    setRejectConfirmOpen(false);
  };

  // For pending review reminders, show accept/reject icons below title
  if (isPendingReview) {
    return (
      <div className={classes.container}>
        <div className={classes.row}>
          <Typography
            className={classes.title}
            sx={{
              fontWeight: 600,
              textDecoration: isCompleted ? 'line-through' : 'none',
            }}
          >
            {reminderTitle}
          </Typography>
        </div>
        <div className={classes.actions}>
          <AlineTooltip arrow placement="top" title="Accept">
            <IconButton
              className={classes.actionButton}
              disabled={acceptLoading || rejectLoading}
              size="small"
              onClick={handleAccept}
            >
              <CheckIcon />
            </IconButton>
          </AlineTooltip>
          <AlineTooltip arrow placement="top" title="Reject">
            <IconButton
              className={classes.actionButton}
              disabled={acceptLoading || rejectLoading}
              size="small"
              onClick={handleRejectClick}
            >
              <RejectChangeXIcon />
            </IconButton>
          </AlineTooltip>
        </div>
        <ConfirmationDialog
          message="Are you sure you want to reject this reminder? This action cannot be undone."
          open={rejectConfirmOpen}
          primaryText="Reject"
          title="Reject reminder?"
          onClickPrimary={handleRejectConfirm}
          onClose={() => setRejectConfirmOpen(false)}
        />
      </div>
    );
  }

  // Regular reminders show complete button before the title
  return (
    <div className={classes.row}>
      <CompleteReminderButton reminder={row} />
      <Typography
        className={classes.title}
        sx={{
          fontWeight: 600,
          textDecoration: isCompleted ? 'line-through' : 'none',
        }}
      >
        {reminderTitle}
      </Typography>
    </div>
  );
}
